import { useState } from "react";
import { usePropertyInventory } from "../../../../../hooks/usePropertyInventory";
import {
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Chip,
  CircularProgress
} from "@mui/material";
import { alpha, Box, Grid } from "@mui/system";
import {
  ArrowBack,
  ArrowForward,
  Business,
  LocationOn,
  Straighten,
  Description,
  Facebook,
  Instagram,
  Security,
  Pool,
  FitnessCenter,
  LocalParking,
  Layers,
} from "@mui/icons-material";
import { CloudUpload, Close } from "@mui/icons-material";
import { searchGeo } from "../../../../../services/geolocationService";
import { CreateApartmentComplex, typeOfComplexOptions } from "../../../../../types/propertyInventory/ApartmentComplex";

const typeOptions = [
  { value: typeOfComplexOptions.Residencial, label: 'Residencial' },
  { value: typeOfComplexOptions.Edificio, label: 'Edificio' },
  { value: typeOfComplexOptions.Condominio, label: 'Condominio' },
  { value: typeOfComplexOptions.Otro, label: 'Otro' },
];
export const Step1ComplexData = () => {
  const { currentStep, setCurrentStep, setApartmentComplex, apartmentComplex,
    complexFiles, setComplexFiles
   } = usePropertyInventory();
  const [geoData, setGeoData] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);

  const handleInputChange = async (field: string, value: any) => {
    if (setApartmentComplex) {
      setApartmentComplex((prev: CreateApartmentComplex) => {
        if (!prev) return prev;
        if (field === 'location') {
          return {
            ...prev,
            location: value,
          };
        }
        return { ...prev, [field]: value };
      });
    }

    if (field === 'location') {
      if (value.length > 2) {
        setLoadingGeo(true);
        setShowSuggestions(true);
        try {
          const response = await searchGeo(value);
          setGeoData(response);
        } catch (error) {
          console.error('Error searching location:', error);
          setGeoData([]);
        } finally {
          setLoadingGeo(false);
        }
      } else {
        setGeoData([]);
        setShowSuggestions(false);
        setLoadingGeo(false);
      }
    }
  };

  const handleLocationSelect = (geo: any) => {
    if (setApartmentComplex) {
      setApartmentComplex((prev: CreateApartmentComplex) => {
        return {
          ...prev,
          location: geo.display_name,
        };
      });
    }
    setShowSuggestions(false);
    setGeoData([]);
  };

  const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);

      if (setApartmentComplex) {
        setApartmentComplex((prev: CreateApartmentComplex) => {
          return {
            ...prev,
            files: [...(prev.files || []), ...files],
          };
        });
        setComplexFiles((prev) => [...prev, ...files]);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    if (setApartmentComplex) {
      setApartmentComplex((prev: CreateApartmentComplex) => {
        return {
          ...prev,
          files: prev.files?.filter((_, i) => i !== index),
        };
      });
      setComplexFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };
  const handleNext = () => {
    if (setApartmentComplex) {
      localStorage.setItem("createApartmentComplex", JSON.stringify(apartmentComplex));
      setApartmentComplex(apartmentComplex);
    }
    setCurrentStep(currentStep + 1);
  };

  const isValid =
    apartmentComplex?.name &&
    apartmentComplex?.typeOfComplex &&
    apartmentComplex.totalFloors &&
    apartmentComplex.totalParkingSpaces &&
    apartmentComplex.location &&
    apartmentComplex.totalFloors > 0 &&
    apartmentComplex.totalParkingSpaces >= 0;

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight="bold" color="#2E7D7A" sx={{ mb: 1 }}>
          Información del Complejo Residencial
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Complete los datos básicos del complejo de apartamentos
        </Typography>

        <Grid container spacing={3}>
          {/* Información Básica */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="#2E7D7A" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <Business sx={{ mr: 1 }} /> Información Básica
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              label="Nombre del Complejo"
              placeholder="Ej: Apartamentos El Sol"
              value={apartmentComplex?.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              InputProps={{
                startAdornment: <Business sx={{ mr: 1, color: '#4DB6AC' }} />,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth required>
              <InputLabel>Tipo de Complejo</InputLabel>
              <Select
                value={apartmentComplex?.typeOfComplex || ''}
                onChange={(e) => handleInputChange('typeOfComplex', e.target.value)}
                label="Tipo de Complejo"
              >
                {typeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              type="number"
              label="Pisos Totales"
              value={apartmentComplex?.totalFloors || ''}
              onChange={(e) => handleInputChange('totalFloors', parseInt(e.target.value) || 0)}
              InputProps={{
                startAdornment: <Layers sx={{ mr: 1, color: '#4DB6AC' }} />,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              required
              type="number"
              label="Espacios de Parqueo"
              value={apartmentComplex?.totalParkingSpaces || ''}
              onChange={(e) => handleInputChange('totalParkingSpaces', parseInt(e.target.value) || 0)}
              InputProps={{
                startAdornment: <LocalParking sx={{ mr: 1, color: '#4DB6AC' }} />,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 12 }}>
            <TextField
              fullWidth
              type="number"
              label="Metros Cuadrados"
              placeholder="Área total del complejo"
              value={apartmentComplex?.squareMeters || ''}
              onChange={(e) => handleInputChange('squareMeters', parseFloat(e.target.value) || undefined)}
              InputProps={{
                startAdornment: <Straighten sx={{ mr: 1, color: '#4DB6AC' }} />,
                endAdornment: <InputAdornment position="end">m²</InputAdornment>,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Descripción"
              placeholder="Describe las características principales del complejo..."
              value={apartmentComplex?.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
              InputProps={{
                startAdornment: <Description sx={{ mr: 1, color: '#4DB6AC', alignSelf: 'flex-start', mt: 1 }} />,
              }}
            />
          </Grid>

          {/* Ubicación */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="#2E7D7A" sx={{ mb: 2, mt: 2, display: 'flex', alignItems: 'center' }}>
              <LocationOn sx={{ mr: 1 }} /> Ubicación
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }} sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              required
              label="Dirección Completa"
              placeholder="Buscar dirección..."
              value={apartmentComplex?.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              onFocus={() => geoData.length > 0 && setShowSuggestions(true)}
              InputProps={{
                startAdornment: <LocationOn sx={{ mr: 1, color: '#4DB6AC' }} />,
              }}
              autoComplete="off"
            />

            {/* Sugerencias de ubicación mejoradas */}
            {showSuggestions && (apartmentComplex?.location?.length || 0) > 2 && (
              <Box
                sx={{
                  position: 'absolute',
                  width: '100%',
                  mt: 1,
                  p: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  boxShadow: 3,
                  zIndex: 1000
                }}
              >
                {loadingGeo ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#4DB6AC' }} />
                    <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                      Buscando direcciones...
                    </Typography>
                  </Box>
                ) : geoData.length > 0 ? (
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {geoData.map((geo: any, index: number) => (
                      <Box
                        key={geo.place_id || index}
                        sx={{
                          px: 2,
                          py: 1.5,
                          cursor: 'pointer',
                          borderBottom: index < geoData.length - 1 ? '1px solid #F5F5F5' : 'none',
                          borderRadius: 1,
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha('#4DB6AC', 0.12),
                          }
                        }}
                        onClick={() => handleLocationSelect(geo)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <LocationOn sx={{ mr: 1, color: '#4DB6AC', fontSize: 20, mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {geo.display_name}
                            </Typography>
                            {geo.type && (
                              <Chip
                                label={geo.type}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  height: 20,
                                  fontSize: '0.7rem',
                                  backgroundColor: alpha('#4DB6AC', 0.1),
                                  color: '#2E7D7A'
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No se encontraron resultados
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          {/* Información de Contacto */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="#2E7D7A" sx={{ mb: 2, mt: 2 }}>
              Información de Contacto
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Facebook"
              placeholder="Usuario o URL de Facebook"
              value={apartmentComplex?.facebook || ''}
              onChange={(e) => handleInputChange('facebook', e.target.value)}
              InputProps={{
                startAdornment: <Facebook sx={{ mr: 1, color: '#4DB6AC' }} />,
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Instagram"
              placeholder="Usuario o URL de Instagram"
              value={apartmentComplex?.instagram || ''}
              onChange={(e) => handleInputChange('instagram', e.target.value)}
              InputProps={{
                startAdornment: <Instagram sx={{ mr: 1, color: '#4DB6AC' }} />,
              }}
            />
          </Grid>

          {/* Amenidades */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="#2E7D7A" sx={{ mb: 2, mt: 2 }}>
              Amenidades y Características
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={apartmentComplex?.hasSecurity || false}
                    onChange={(e) => handleInputChange('hasSecurity', e.target.checked)}
                    sx={{
                      color: '#4DB6AC',
                      '&.Mui-checked': { color: '#2E7D7A' }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Security sx={{ mr: 0.5, color: '#4DB6AC' }} />
                    Seguridad 24/7
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={apartmentComplex?.hasPool || false}
                    onChange={(e) => handleInputChange('hasPool', e.target.checked)}
                    sx={{
                      color: '#4DB6AC',
                      '&.Mui-checked': { color: '#2E7D7A' }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Pool sx={{ mr: 0.5, color: '#4DB6AC' }} />
                    Piscina
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={apartmentComplex?.hasGym || false}
                    onChange={(e) => handleInputChange('hasGym', e.target.checked)}
                    sx={{
                      color: '#4DB6AC',
                      '&.Mui-checked': { color: '#2E7D7A' }
                    }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FitnessCenter sx={{ mr: 0.5, color: '#4DB6AC' }} />
                    Gimnasio
                  </Box>
                }
              />
            </Box>
          </Grid>
          {/* Multimedia */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" color="#2E7D7A" sx={{ mb: 2, mt: 2 }}>
              Imágenes y Videos
            </Typography>
          </Grid>

          {/* Botón único para subir multimedia */}
          <Grid size={{ xs: 12 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              fullWidth
              sx={{
                borderColor: '#4DB6AC',
                color: '#2E7D7A',
                py: 1.5,
                '&:hover': {
                  borderColor: '#26A69A',
                  backgroundColor: alpha('#4DB6AC', 0.05)
                }
              }}
            >
              Subir Imágenes y Videos
              <input
                type="file"
                hidden
                multiple
                accept="image/*,video/*"
                onChange={handleMediaUpload}
              />
            </Button>
          </Grid>

          {/* Preview de multimedia */}
          {complexFiles && complexFiles.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                Multimedia ({complexFiles.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {complexFiles.map((img, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      width: 120,
                      height: 120,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: '#E0E0E0',
                      '&:hover .delete-overlay': {
                        opacity: 1
                      }
                    }}
                  >
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <Box
                      className="delete-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleRemoveFile(index)}
                    >
                      <Close sx={{ color: 'white', fontSize: 32 }} />
                    </Box>
                    <Chip
                      label={img.name.length > 15 ? img.name.substring(0, 15) + '...' : img.name}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4,
                        right: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.65rem',
                        height: 20
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
          )}  
        </Grid>

        {/* Botones de navegación */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, mt: 4, pt: 3, borderTop: '1px solid #E0E0E0' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            disabled
            sx={{
              borderColor: '#E0E0E0',
              color: '#999'
            }}
          >
            Anterior
          </Button>

          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            disabled={!isValid}
            sx={{
              backgroundColor: '#4DB6AC',
              '&:hover': {
                backgroundColor: '#26A69A',
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
              }
            }}
          >
            Siguiente: Configurar Apartamentos
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};